import React, { useEffect, useState } from 'react';
import { OverlayContainer } from '@react-aria/overlays';
import { Item } from '@react-stately/collections';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { BoxButton, TextButton } from '@riac/design-system';
import { FaBook, FaCog, FaPrint } from 'react-icons/fa';

import { Input, SVGCheckbox } from '@riac/design-system';
import { FlexModal } from '@riac/design-system';
import {
  EBookDownloadButton,
  EBookDownloadContainer,
  EbookModalBody,
  PdfAdvancedOptionsToggle,
  PdfOptionsForm,
  PdfOptionsFormSelect,
} from 'src/components/EBookDownload/EBookDownload.style';
import { InputError } from '@riac/design-system';
import { useFetch } from 'src/lib/FetchContext';
import { LoginToUse } from '../LoginToUse/LoginToUse';
import { SearchPostData } from '../../pages/CatalogPage/CatalogPage.i';
import { ThingSwitcher, Thing } from '@riac/design-system';
import { validateEmail } from '@riac/core/util';
import { Spinner } from '@riac/design-system';
import { Row } from '@riac/design-system';

export type EBookDownloadProps = React.PropsWithChildren<{
  searchPostData: SearchPostData,
  pdfTitle?: string,
  auctionId: number,
  title: string,
  label?: string,
  forceFilters?: boolean,
  isDisabled?: boolean,
}>;

export const EBookDownload = ({
  searchPostData,
  pdfTitle = undefined,
  auctionId,
  title,
  label='',
  forceFilters = false,
  isDisabled = false,
}: EBookDownloadProps): JSX.Element => {
  const { fetch } = useFetch();

  const [tooManyRequests, setTooManyRequests] = useState<string>('');
  const [includeFilters, setIncludeFilters] = useState(true);
  const [loading, setLoading] = useState<boolean>(false);

  const epubExportModalState = useOverlayTriggerState({});
  const [epubExportModalDisplay, setEpubExportModalDisplay] = useState<string>('');
  const [epubDownload, setEpubDownload] = useState<boolean | Blob>(false);
  const [epubGenerationError, setEpubGenerationError] = useState<string>('');

  const pdfExportModalState = useOverlayTriggerState({});
  const [pdfExportModalDisplay, setPdfExportModalDisplay] = useState<string>('');
  const [emailAddress, setEmailAddress] = useState('');
  const [showPdfAdvancedOptions, setShowPdfAdvancedOptions] = useState(false);
  const [paperSize, setPaperSize] = useState('letter');
  const [pageOrientation, setPageOrientation] = useState('portrait');
  const [fontSize, setFontSize] = useState('12');
  const [marginSize, setMarginSize] = useState('32');
  const [inputErrors, setInputErrors] = useState([]);
  const [pdfGenerationError, setPdfGenerationError] = useState<string>('');

  useEffect(() => {
    setPaperSize('letter');
    setShowPdfAdvancedOptions(false);
    setEpubDownload(false);
    setTooManyRequests('');
  }, [pdfExportModalState.isOpen, epubExportModalState.isOpen]);

  const startPDFGeneration = async (): Promise<void> => {
    setPdfGenerationError('');
    setTooManyRequests('');
    if(!paperSize) {
      setInputErrors(['You must select a paper size']);
      return;
    }
    else if(!validateEmail(emailAddress))
    {
      setInputErrors(['You must enter a valid email address']);
      return;
    }
    else
    {
      setInputErrors([]);
      setLoading(true);
    }
    const pdfResponse = await fetch(`/api/ebook/pdf/start/${auctionId}`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        searchPostData: includeFilters ? searchPostData : { auctionid: auctionId },
        pdfOptions: {
          pdfTitle: pdfTitle,
          paperSize: paperSize,
          pageOrientation: pageOrientation,
          fontSize: fontSize,
          marginSize: marginSize,
        },
        email: encodeURIComponent(emailAddress),
      }),
    });

    setLoading(false);

    if(pdfResponse.status === 429) {
      setTooManyRequests('Too many requests today. Try again tomorrow');
      return;
    }
    const pdfResult = await pdfResponse.json();

    setPdfExportModalDisplay('result');
    if(pdfResult.success == false) {
      if(pdfResult.error == 'No Items Returned') {
        // eslint-disable-next-line max-len
        setPdfGenerationError('Cannot Create PDF With Blank Search Results.  Try Another Search Or Deselect "Include only the lots that match my filters"');
      }
    }
  };

  const startEpubDownload = async (): Promise<void> => {
    setTooManyRequests('');
    setEpubGenerationError('');
    setEpubDownload(true);
    setLoading(true);

    const epubResponse = await fetch(`/api/ebook/epub/download/${auctionId}/${encodeURIComponent(title)}`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        searchPostData: includeFilters ? searchPostData : { auctionid: auctionId },
      }),
    });

    setLoading(false);
    if(epubResponse.status === 429) {
      setTooManyRequests('Too many requests today. Try again tomorrow');
      setEpubDownload(false);
      return;
    }

    const epubResult = await epubResponse.json();

    if(epubResult.status == false) {
      setEpubExportModalDisplay('result');
      if(epubResult.error == 'No Items Returned') {
        // eslint-disable-next-line max-len
        setEpubGenerationError('Cannot Create EPUB With Blank Search Results.  Try Another Search Or Deselect "Include only the lots that match my filters"');
      }
    } else {
      const a = document.createElement('a');
      a.style.display = 'none';
      a.setAttribute('href', epubResult.url);
      a.setAttribute('download', `${title}.epub`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setEpubDownload(false);
      epubExportModalState.close();
    }
  };

  return (
    <EBookDownloadContainer>
      <LoginToUse
        overlayText = 'Log in to download catalog'
      >
        <div>
          <EBookDownloadButton onPress={() => {
            setEpubExportModalDisplay('input');
            epubExportModalState.open();
          }}>
            <FaBook/> Download {label} as EPub
          </EBookDownloadButton>
        </div>
        <div>
          <EBookDownloadButton onPress={() => {
            setPdfExportModalDisplay('input');
            pdfExportModalState.open();
          }}>
            <FaPrint/> Download {label} as PDF for printing
          </EBookDownloadButton>
        </div>
      </LoginToUse>

      {epubExportModalState.isOpen && (
        <OverlayContainer>
          <FlexModal
            isOpen
            title={`${label} EPub Download`}
            onClose={() => epubExportModalState.close()}
            showCloseButton
            isDismissable
          >
            <EbookModalBody>
              <InputError error = {tooManyRequests}/>
              <ThingSwitcher thing = {epubExportModalDisplay}>
                <Thing label = 'input'>
                  {(forceFilters != true) && (
                    <SVGCheckbox
                      label = 'Include only the lots that match my filters'
                      isSelected = {includeFilters}
                      isDisabled = {loading}
                      onChange = {() => setIncludeFilters((prev) => !prev)}
                    />
                  )}
                  <Row gap = '2em'>
                    <BoxButton
                      isDisabled = {isDisabled || loading}
                      onPress={
                        epubDownload
                          ? undefined
                          : startEpubDownload
                      }
                    >
                      Download EPub
                    </BoxButton>
                    <div>
                      <Spinner
                        inverted
                        loading = {loading}
                      />
                    </div>
                  </Row>

                </Thing>
                <Thing label = 'result'>
                  <InputError error = {epubGenerationError}/>
                </Thing>
              </ThingSwitcher>
            </EbookModalBody>
          </FlexModal>
        </OverlayContainer>
      )}
      {pdfExportModalState.isOpen && (
        <OverlayContainer>
          <FlexModal
            isOpen
            title={`${label} PDF Generator`}
            onClose={() => pdfExportModalState.close()}
            showCloseButton
            isDismissable
          >
            <EbookModalBody>
              <InputError error = {tooManyRequests}/>
              <ThingSwitcher thing = {pdfExportModalDisplay}>
                <Thing label = 'input'>
                  <div>
                    <Input
                      type='text'
                      label='Email Address'
                      value={emailAddress}
                      onChange={(value) => setEmailAddress(value)}
                      autoComplete = 'email'
                      disabled = {isDisabled || loading}
                      inverted
                    />
                    <PdfAdvancedOptionsToggle>
                      <TextButton
                        blueText
                        isDisabled = {isDisabled || loading}
                        onPress={() => {setShowPdfAdvancedOptions(!showPdfAdvancedOptions);}}
                      >
                        <FaCog/>{`${showPdfAdvancedOptions ? 'Hide' : 'Show'} Advanced Options`}
                      </TextButton>
                    </PdfAdvancedOptionsToggle>
                  </div>
                  <div>
                    {(showPdfAdvancedOptions) && (
                      <PdfOptionsForm>
                        <PdfOptionsFormSelect
                          name='size'
                          label='Paper Size'
                          placeholder='Select a paper size...'
                          onChange={(value) => setPaperSize(value.toString())}
                          value={paperSize}
                          isDisabled = {loading}
                          inverted
                        >
                          <Item key='letter'>Letter</Item>
                          <Item key='legal'>Legal</Item>
                        </PdfOptionsFormSelect>
                        <PdfOptionsFormSelect
                          name='pageOrientation'
                          label='Page Orientation'
                          placeholder='Select a page orientation...'
                          onChange={(value) => setPageOrientation(value.toString())}
                          value={pageOrientation}
                          isDisabled = {loading}
                          inverted
                        >
                          <Item key='portrait'>Portrait</Item>
                          <Item key='landscape'>Landscape</Item>
                        </PdfOptionsFormSelect>
                        <PdfOptionsFormSelect
                          name='fontSize'
                          label='Font Size'
                          placeholder='Select a font size...'
                          onChange={(value) => setFontSize(value.toString())}
                          value={fontSize}
                          isDisabled = {loading}
                          inverted
                        >
                          <Item key={8}>Smallest (8pt)</Item>
                          <Item key={10}>Smaller (10pt)</Item>
                          <Item key={12}>Small (12pt)</Item>
                          <Item key={16}>Medium (16pt)</Item>
                          <Item key={18}>Large (18pt)</Item>
                          <Item key={20}>Extra-Large (20pt)</Item>
                        </PdfOptionsFormSelect>
                        <PdfOptionsFormSelect
                          name='marginSize'
                          label='Margin Size'
                          placeholder='Select a margin size...'
                          onChange={(value) => setMarginSize(value.toString())}
                          value={marginSize}
                          isDisabled = {loading}
                          inverted
                        >
                          <Item key={8}>Extra Small (8px)</Item>
                          <Item key={16}>Small (16px)</Item>
                          <Item key={32}>Medium (32px)</Item>
                          <Item key={72}>Large (72px)</Item>
                        </PdfOptionsFormSelect>
                      </PdfOptionsForm>
                    )}
                  </div>
                  <div>
                    {(forceFilters != true) && (
                      <SVGCheckbox
                        label = 'Include only the lots that match my filters'
                        isSelected = {includeFilters}
                        isDisabled = {isDisabled || loading}
                        onChange = {() => setIncludeFilters((prev) => !prev)}
                      />
                    )}
                  </div>
                  <div>
                    The email address you enter into this form will exclusively be used to send
                    you your {label} PDF when it is complete. It will not be stored or transfered
                    to third parties.
                  </div>
                  <div>
                    {inputErrors.map((iError) => (
                      <InputError
                        error={iError}
                      />
                    ))}
                  </div>
                  <Row gap = '2em'>
                    <BoxButton
                      isDisabled={isDisabled || loading}
                      onPress={startPDFGeneration}
                    >
                      Generate my PDF
                    </BoxButton>
                    <div>
                      <Spinner
                        inverted
                        loading = {loading}
                      />
                    </div>
                  </Row>
                </Thing>
                <Thing label = 'result'>
                  {pdfGenerationError
                    ? <InputError error = {pdfGenerationError}/>
                    : (
                      <div>
                        Your {label} PDF is being created and a link will be emailed to '{emailAddress}'
                      </div>
                    )
                  }
                  <div>
                    <BoxButton
                      onPress={() => {
                        setPdfExportModalDisplay('input');
                        setPaperSize('letter');
                      }}
                    >
                      Generate a new PDF
                    </BoxButton>
                  </div>
                </Thing>
              </ThingSwitcher>
            </EbookModalBody>
          </FlexModal>
        </OverlayContainer>
      )}
    </EBookDownloadContainer>
  );
};
